<template>
  <div
    data-qa="footer"
    class="core-footer bg-secondary py-6"
  >
    <div class="container core-footer__inner">
      <div class="d-flex flex-column ga-6">
        <a :href="reverseFull({ name: 'prodoctorov_main' })">
          <img
            :src="isRussia ? `${staticPath}_v1/pd/logos/ui-kit/prodoctorov-logo.svg` : `${mediaPath}${context.request.country.site_logo}`"
            :alt="t('Логотип ПроДокторов')"
            class="image-mono"
            loading="lazy"
            height="28"
          >
        </a>
        <div
          data-qa="footer_menu"
          class="d-flex flex-wrap justify-space-between"
        >
          <div
            v-for="(data, i) in footerLinks"
            :key="`footer-col-${i}`"
            class="core-footer__column"
          >
            <div
              v-for="({ title, items, hasInCISGroup }, j) in data"
              :key="title"
            >
              <div
                v-if="isRussia || (!isRussia && hasInCISGroup)"
                :class="{ 'mt-5': j > 0 }"
              >
                <div
                  v-if="title"
                  class="text-subtitle-1 text--text mb-4"
                >
                  {{ title }}
                </div>
                <div
                  v-for="item in items"
                  :key="item.name"
                >
                  <template
                    v-if="(isRussia && item.hasInPD) || (!isRussia && item.hasInCIS)"
                  >
                    <CorePRG
                      v-if="item.isPRG"
                      target="_self"
                      data-qa="footer_href"
                      class="text-body-1 link-decoration-none text-secondary--text mb-4"
                      :url="item.href"
                    >
                      <div class="text-left">
                        {{ item.name }}
                      </div>
                    </CorePRG>
                    <a
                      v-else
                      :key="item.name"
                      :href="item.href"
                      data-qa="footer_href"
                      class="d-table text-body-1 link-decoration-none text-secondary--text mb-4 text-left"
                    >
                      {{ item.name }}
                    </a>
                  </template>
                </div>
                <div
                  v-if="isRussia && footerLinks.length - 1 === i"
                  class="d-flex ga-4"
                >
                  <CorePRG
                    target="_blank"
                    :url="`${staticPath}_v1/pd/documents/pdf/Единый_реестр_российских_программ.pdf`"
                  >
                    <img
                      :src="`${staticPath}_v1/pd/logos/ru-reestr-programs.png`"
                      loading="lazy"
                      :alt="t('Единый реестр российских программ')"
                      width="45"
                      height="54"
                    >
                  </CorePRG>
                  <img
                    :src="`${staticPath}_v1/pd/icons/plus-18-info-color.svg`"
                    alt="18+"
                    width="38"
                    height="38"
                    loading="lazy"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          data-qa="footer_info"
          class="d-flex flex-column ga-2"
        >
          <div class="text-body-2 text-info--text">
            {{ companyInfoText }}
          </div>
          <div
            v-if="isRussia"
            class="text-body-2 text-info--text"
          >
            {{ roskomnadzorRegistrationText }}<br>
            {{ roskomnadzorDetailsText }}
          </div>
          <div class="text-body-2 text-info--text">
            {{ medicalDisclaimerText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { translate } from 'src/layouts/core-layout/_functions/translate'
import type { ContextStore } from 'src/layouts/core-layout/types'
import { useURLStore, useContextStore } from 'src/store'
import CorePRG from 'src/core/CorePRG/CorePRG.vue'

// @ts-ignore
const { context, urlLang }: ContextStore = useContextStore()
const { reverseFull, reverseFullL10N } = useURLStore()
const t = (key: string, interpolation?: Record<string, unknown>) => translate(key, interpolation)

const currentYear = new Date().getFullYear()
const companyInfoText = computed(() => (
  `${t('© 2011–')}${currentYear} ${t('МедРокет')} ${context.company.taxpayer_identification_number}`
))

const {
  src: staticPath,
  media: mediaPath,
} = context.global.static
const {
  siteNamePD,
  siteNamePT,
  siteNamePB,
  siteNameMT,
  siteNameML,
  siteNameMF,
  siteNameHelp,
  siteNameAward,
  siteNameExpert,
  siteNameVacancies,
  siteNameMedrocket,
} = context.global.sites

const { is_russia: isRussia } = context.request.country

const roskomnadzorRegistrationText = computed(() => translate(
  'Зарегистрировано Федеральной службой по надзору в сфере связи, '
  + 'информационных технологий и массовых коммуникаций (Роскомнадзор).',
))

const roskomnadzorDetailsText = computed(() => translate(
  'Регистрационный номер Эл № ФС 77 - 89293 от 01.04.2025 г. Учредитель — '
  + 'ООО «МЕДРОКЕТ» ОГРН 1122311003760. Адрес редакции: 350015, Краснодарский край, '
  + 'г. Краснодар, ул. Буденного, д. 182, офис 87.',
))

const medicalDisclaimerText = computed(() => translate(
  'Информация, представленная на сайте, не может быть использована для постановки диагноза, '
  + 'назначения лечения и не заменяет прием врача.',
))

type FooterData = {
  title: string,
  hasInCISGroup: boolean
  items: Array<{
    href: string
    name: string
    isPRG: boolean
    hasInPD: boolean
    hasInCIS: boolean
  }>
}

const footerLinks = computed<Array<Array<FooterData>>>(() => [
  [
    {
      title: '',
      hasInCISGroup: true,
      items: [
        {
          href: siteNameMedrocket,
          name: t('Компания МедРокет'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: reverseFullL10N({ name: 'about_us', args: { language: urlLang } }),
          name: isRussia ? t('О ПроДокторов') : t('О YDoc'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: reverseFullL10N({ name: 'document', args: { language: urlLang } }),
          name: isRussia ? t('Редакция и юрлицо') : t('Реквизиты компании'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: new URL('info/pressa/', siteNamePD).href,
          name: t('О нас говорят'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: new URL('info/conf/', siteNamePD).href,
          name: t('Выступления'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: new URL('info/legal-case/', siteNamePD).href,
          name: t('Битва за отзывы'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: new URL('info/rate-fraud-free/', siteNamePD).href,
          name: t('Отзывы без обмана'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: new URL('info/ethical-commission/', siteNamePD).href,
          name: t('Этический комитет'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: siteNameVacancies,
          name: t('Вакансии'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: new URL('info/socials/', siteNamePD).href,
          name: t('Соцсети'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: reverseFullL10N({ name: 'contacts', args: { language: urlLang } }),
          name: t('Контакты'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
      ],
    },
  ],
  [
    {
      title: t('Наши продукты'),
      hasInCISGroup: true,
      items: [
        {
          href: 'https://days.medrocket.me/',
          name: t('Конференция Дни МедРокет'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: siteNameAward,
          name: t('Премия ПроДокторов'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: reverseFull({ name: 'memorial' }),
          name: t('Доска памяти врачей'),
          isPRG: true,
          hasInPD: context.is_show_memorial,
          hasInCIS: false,
        },
        {
          href: siteNamePD,
          name: t('ПроДокторов'),
          isPRG: true,
          hasInPD: false,
          hasInCIS: true,
        },
        {
          href: siteNamePT,
          name: t('ПроТаблетки'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: siteNamePB,
          name: t('ПроБолезни'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: siteNameMF,
          name: t('МедФлекс'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: siteNameMT,
          name: t('МедТочка'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: siteNameML,
          name: t('МедЛок'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: true,
        },
      ],
    },
  ],
  [
    {
      title: t('Помощь'),
      hasInCISGroup: true,
      items: [
        {
          href: siteNameHelp,
          name: t('База знаний'),
          isPRG: true,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: reverseFullL10N({ name: 'info_register', args: { language: urlLang, page_type: 'doctor-about' } }),
          name: t('Регистрация врача'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: reverseFullL10N({ name: 'info_register', args: { language: urlLang, page_type: 'lpu-about' } }),
          name: t('Регистрация клиники'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
      ],
    },
    {
      title: t('Партнёрам'),
      hasInCISGroup: false,
      items: [
        {
          href: siteNameExpert,
          name: t('Клиникам'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: new URL('vrachu/', siteNameExpert).href,
          name: t('Врачам'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: `${staticPath}_v1/pd/documents/pdf/footer/Исследование_ПроДокторов_2025.pdf`,
          name: t('Исследование поведения \n пациента'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
      ],
    },
  ],
  [
    {
      title: t('Карта сайта'),
      hasInCISGroup: true,
      items: [
        {
          href: reverseFullL10N({ name: 'town_select', args: { language: urlLang } }),
          name: t('Города'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: reverseFullL10N({ name: 'regions', args: { language: urlLang } }),
          name: t('Регионы'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: reverseFullL10N({ name: 'map_doctor', args: { language: urlLang } }),
          name: t('Врачи'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: reverseFullL10N({ name: 'map_lpu', args: { language: urlLang } }),
          name: t('Клиники'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: new URL('services/', siteNamePD).href,
          name: t('Услуги'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
      ],
    },
  ],
  [
    {
      title: t('Условия использования'),
      hasInCISGroup: true,
      items: [
        {
          href: new URL('info/terms-of-use/', siteNamePD).href,
          name: t('Пользовательское соглашение'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: new URL('info/privacy-policy/', siteNamePD).href,
          name: t('Обработка персональных данных'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: true,
        },
        {
          href: new URL('info/publishing-policy/', siteNamePD).href,
          name: t('Публикация материалов'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: new URL('info/authors-protection/', siteNamePD).href,
          name: t('Условия программы защиты авторов отзывов'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
        {
          href: new URL('info/rate-fraud-free-rules/', siteNamePD).href,
          name: t('Условия программы "Отзывы без обмана"'),
          isPRG: false,
          hasInPD: true,
          hasInCIS: false,
        },
      ],
    },
  ],
])
</script>

<style lang="scss">
@import 'src/styles/index';
@import 'src/styles/typography';
@import 'src/styles/breakpoints';

.core-footer__inner {
  container-type: inline-size;
  container-name: core-footer;
}

.core-footer__column {
  width: 20%;

  @include media-down(md) {
    width: 50%;
    margin-bottom: 12px;
  }

  @include media-down(sm) {
    width: 100%;
  }
}

// 960px включительно: у .container при окне ≤ 1120px max-width как раз 960px.
@container core-footer (max-width: 960px) {
  .core-footer__column {
    width: 50%;
    margin-bottom: 12px;
  }
}

@container core-footer (max-width: 599.98px) {
  .core-footer__column {
    width: 100%;
  }
}

[data-qa="footer"] {
  .v-expansion-panel-header,
  .v-expansion-panel-content__wrap {
    padding: 0;
  }

  .v-expansion-panel--active {
    .v-expansion-panel-header__icon {
      transform: rotate(180deg);
    }
  }
}
</style>
